import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setModal } from 'modules/_shared/redux/modalActions';
import { downloadPartnersTable } from 'modules/partners/utils/downloadPartrnersTable';

import { DownloadTableModalContextType } from '../types/DownloadTableModalContextType';
import { downloadTableModalInitValue } from '../constants/downloadTableModalInitValue';

const DownloadTableModalContext = createContext<DownloadTableModalContextType>(
  downloadTableModalInitValue
);

export const DownloadTableModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [pdfFormat, setPdfFormat] = useState<boolean>(false);
  const [excelFormat, setExcelFormat] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangePdfFormat = () => {
    setPdfFormat((prev) => !prev);
  };

  const handleChangeExcelFormat = () => {
    setExcelFormat((prev) => !prev);
  };

  const handleDownloadTable = async () => {
    try {
      setIsLoading(true);
      const societyId = actualSociety?.['_id'];

      if (!societyId) {
        handleCloseModal();
        return;
      }

      const fileName = `${t('PartnersList')} ${actualSociety.name}`;

      await downloadPartnersTable({
        fileName,
        societyId,
        formats: {
          pdf: pdfFormat,
          excel: excelFormat,
        },
      });
    } catch (error) {
      setIsLoading(false);
    }
    handleCloseModal();
  };

  return (
    <DownloadTableModalContext.Provider
      value={{
        data: {
          pdfFormat,
          excelFormat,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangePdfFormat,
          handleChangeExcelFormat,
          handleDownloadTable,
        },
      }}
    >
      {children}
    </DownloadTableModalContext.Provider>
  );
};

export const useDownloadTableModal = () => {
  const context = useContext(DownloadTableModalContext);
  if (!context) {
    throw new Error(
      'useDownloadTableModal must be used within a DownloadTableModalProvider'
    );
  }
  return context;
};
